/**
 * Documentation navbar component
 */

import {useState, useEffect} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {ROUTES} from '../../config/constants';
import {SearchModal} from './SearchModal';

interface DocsNavbarProps {
  onMenuClick?: () => void;
}

export function DocsNavbar({onMenuClick}: DocsNavbarProps) {
  const location = useLocation();
  const [showSearchModal, setShowSearchModal] = useState(false);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearchModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navLinks = [
    { label: 'Docs', path: '/docs', active: location.pathname.startsWith('/docs') && !location.pathname.startsWith('/docs/api') },
    { label: 'API', path: '/docs/api/overview', active: location.pathname.startsWith('/docs/api') },
    { label: 'Learn', path: ROUTES.RESOURCES, active: location.pathname === ROUTES.RESOURCES },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 h-14">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex items-center h-full">
          {/* Left: Logo + Nav Links */}
          <div className="flex items-center space-x-8 flex-shrink-0">
            <Link to={ROUTES.HOME} className="flex items-center space-x-2">
              <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-base">V</span>
              </div>
              <span className="font-heading font-bold text-lg text-textPrimary">VoxaPilot</span>
            </Link>

            {/* Nav Links */}
            <div className="hidden md:flex items-center space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-base font-medium transition-colors ${
                    link.active
                      ? 'text-orange-600'
                      : 'text-textSecondary hover:text-textPrimary'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Center: Search Bar + Ask AI */}
          <div className="hidden lg:flex items-center justify-center flex-1 px-8">
            <div className="flex items-center space-x-2 w-full max-w-2xl">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <button
                  onClick={() => setShowSearchModal(true)}
                  className="w-full text-left pl-10 pr-20 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-textSecondary hover:border-gray-300 transition-colors"
                >
                  Search docs...
                </button>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded">⌘K</kbd>
                </div>
              </div>
              <button className="px-3 py-2 text-sm font-medium text-textSecondary hover:text-textPrimary transition-colors bg-gray-50 border border-gray-200 rounded-md flex items-center space-x-1">
                <span>Ask AI</span>
                <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded">⌘I</kbd>
              </button>
            </div>
          </div>

          {/* Search Modal */}
          <SearchModal isOpen={showSearchModal} onClose={() => setShowSearchModal(false)} />

          {/* Right: Sign In */}
          <div className="flex items-center flex-shrink-0 ml-auto">
            <Link to={ROUTES.SIGN_IN}>
              <button className="text-base font-medium text-textSecondary hover:text-textPrimary transition-colors px-4 py-2">
                Sign in
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 ml-4"
            onClick={onMenuClick}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6 text-textPrimary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
    </nav>
  );
}

